import React from 'react';
import clsx from 'clsx';
import { Button, Badge } from '@randompedestrian07/ui';
import { Loader2, type LucideIcon } from 'lucide-react';

interface StatusBadge {
    label: string;
    status?: string;
    showPulse?: boolean;
}

interface Tab {
    id: string;
    label: string;
    icon: React.ReactNode;
}

interface BotPageLayoutProps {
    icon: React.ReactNode;
    iconColorClass?: string;
    title: string;
    subtitle?: string;
    statusBadges?: StatusBadge[];
    isLoading?: boolean;
    tabs: Tab[];
    activeTab: string;
    onTabChange: (tabId: string) => void;
    rightContent?: React.ReactNode;
    children: React.ReactNode;
    maxWidth?: string;
}

const getStatusBadgeClass = (status?: string) => {
    if (!status) return 'text-muted';
    const s = status.toLowerCase();
    if (s === 'healthy' || s === 'online' || s === 'active') return 'text-green bg-green/10 border-green/20';
    if (s === 'degraded' || s === 'warning') return 'text-warning bg-warning/10 border-warning/20';
    if (s === 'unhealthy' || s === 'offline' || s === 'error') return 'text-red bg-red/10 border-red/20';
    return 'text-muted bg-panel-soft border-stroke';
};

export const BotPageLayout: React.FC<BotPageLayoutProps> = ({
    icon,
    iconColorClass = 'bg-accent/10',
    title,
    subtitle,
    statusBadges = [],
    isLoading = false,
    tabs,
    activeTab,
    onTabChange,
    rightContent,
    children,
    maxWidth = '1600px',
}) => {
    return (
        <div
            className="p-8 mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500"
            style={{ maxWidth }}
        >
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                <div className="flex items-center gap-5">
                    <div className={clsx("p-4 rounded-2xl shadow-inner", iconColorClass)}>
                        {icon}
                    </div>
                    <div>
                        <h1 className="text-4xl font-extrabold flex items-center gap-3 text-text tracking-tight">
                            {title}
                            {subtitle && (
                                <span className="text-muted/40 font-normal text-2xl hidden md:inline">{subtitle}</span>
                            )}
                        </h1>
                        {(statusBadges.length > 0 || isLoading) && (
                            <div className="flex items-center gap-3 mt-2">
                                {statusBadges.map((badge, idx) => (
                                    <Badge
                                        key={idx}
                                        variant="secondary"
                                        className={clsx(
                                            "bg-opacity-10 border-stroke font-bold transition-all px-3 py-1 rounded-full",
                                            getStatusBadgeClass(badge.status)
                                        )}
                                    >
                                        {badge.showPulse !== false && (
                                            <div className="w-1.5 h-1.5 rounded-full bg-current mr-2 animate-pulse" />
                                        )}
                                        {badge.label}: {badge.status?.toUpperCase() || 'UNKNOWN'}
                                    </Badge>
                                ))}
                                {isLoading && <Loader2 className="animate-spin text-accent" size={14} />}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
                    <div className="flex bg-panel-strong/40 p-1.5 rounded-2xl border border-stroke backdrop-blur-md shadow-lg">
                        {tabs.map((tab) => (
                            <Button
                                key={tab.id}
                                variant="ghost"
                                className={clsx(
                                    "rounded-xl px-6 h-10 transition-all font-bold",
                                    activeTab === tab.id
                                        ? "!bg-accent !text-black shadow-lg shadow-accent/20 hover:!bg-accent/90"
                                        : "text-muted hover:text-text hover:bg-white/5"
                                )}
                                onClick={() => onTabChange(tab.id)}
                            >
                                <span className="mr-2">{tab.icon}</span>
                                {tab.label}
                            </Button>
                        ))}
                    </div>
                    {rightContent}
                </div>
            </div>

            {children}
        </div>
    );
};
